/**
 * Opening hours: one row per weekday with a "Closed" checkbox and one or
 * more open/close times (a split day, e.g. 8–12 and 13–17, is up to three
 * ranges). Rows wrap at phone width. The API stores
 * `{ mon: [["08:00","17:00"]], …, emergency_24_7 }`; a closed day is an empty
 * list, and a week with every day closed and no 24/7 line is "no hours".
 */
import { useId } from 'react';
import type { FieldIssue } from '../api/errors';
import type { HostedPageHours, HostedPageWeekday } from '../api/types';
import { HOSTED_PAGE_WEEKDAYS } from '../api/types';
import { HHMM_RE, WEEKDAY_LABELS, emptyHours } from './model';

export interface DayForm {
  open: boolean;
  ranges: Array<[string, string]>;
}
export type WeekForm = Record<HostedPageWeekday, DayForm>;

const DEFAULT_RANGE: [string, string] = ['08:00', '17:00'];
const MAX_RANGES = 3;

export function weekFromHours(hours: HostedPageHours | null): WeekForm {
  const week = {} as WeekForm;
  for (const d of HOSTED_PAGE_WEEKDAYS) {
    const ranges = hours?.[d] ?? [];
    week[d] = ranges.length ? { open: true, ranges: ranges.map((r) => [r[0], r[1]]) } : { open: false, ranges: [[...DEFAULT_RANGE]] };
  }
  return week;
}

/** null when no day is open and there is no 24/7 line. */
export function hoursFromWeek(week: WeekForm, emergency: boolean): HostedPageHours | null {
  const out = emptyHours();
  let any = false;
  for (const d of HOSTED_PAGE_WEEKDAYS) {
    const day = week[d];
    if (day.open) {
      out[d] = day.ranges.map((r) => [r[0], r[1]]);
      any = true;
    }
  }
  out.emergency_24_7 = emergency;
  return any || emergency ? out : null;
}

export function validateWeek(week: WeekForm): FieldIssue[] {
  const issues: FieldIssue[] = [];
  for (const d of HOSTED_PAGE_WEEKDAYS) {
    const day = week[d];
    if (!day.open) continue;
    const label = WEEKDAY_LABELS[d];
    for (const [open, close] of day.ranges) {
      if (!HHMM_RE.test(open) || !HHMM_RE.test(close)) {
        issues.push({ path: `hours.${d}`, message: `${label}: enter an opening and a closing time, or tick Closed.` });
        break;
      }
      if (close <= open) {
        issues.push({ path: `hours.${d}`, message: `${label}: the closing time must be after the opening time.` });
        break;
      }
    }
  }
  return issues;
}

function DayRow({
  day,
  value,
  onChange,
  issues,
  disabled,
}: {
  day: HostedPageWeekday;
  value: DayForm;
  onChange: (next: DayForm) => void;
  issues: FieldIssue[];
  disabled?: boolean;
}) {
  const id = useId();
  const label = WEEKDAY_LABELS[day];
  const errId = issues.length ? `${id}-err` : undefined;
  const setRange = (i: number, which: 0 | 1, v: string) =>
    onChange({ ...value, ranges: value.ranges.map((r, j) => (j === i ? (which === 0 ? [v, r[1]] : [r[0], v]) : r)) });
  return (
    <div role="group" aria-labelledby={`${id}-day`} className="stack-sm" style={{ padding: '8px 0', borderTop: '1px solid var(--line)' }}>
      <div className="row" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <span id={`${id}-day`} style={{ fontWeight: 600, minWidth: 96 }}>
          {label}
        </span>
        <span className="check" style={{ alignItems: 'center' }}>
          <input id={`${id}-closed`} type="checkbox" checked={!value.open} disabled={disabled} onChange={(e) => onChange({ ...value, open: !e.target.checked })} />
          <label htmlFor={`${id}-closed`}>
            Closed<span className="visually-hidden"> on {label}</span>
          </label>
        </span>
      </div>
      {value.open ? (
        <div className="stack-sm">
          {value.ranges.map((r, i) => {
            const n = value.ranges.length > 1 ? ` (hours ${i + 1})` : '';
            return (
              <div key={i} className="row" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <input
                  type="time"
                  className="input"
                  style={{ width: 'auto', minWidth: 120 }}
                  aria-label={`${label} opens${n}`}
                  aria-invalid={issues.length > 0 || undefined}
                  aria-describedby={errId}
                  value={r[0]}
                  disabled={disabled}
                  onChange={(e) => setRange(i, 0, e.target.value)}
                />
                <span aria-hidden="true">to</span>
                <input
                  type="time"
                  className="input"
                  style={{ width: 'auto', minWidth: 120 }}
                  aria-label={`${label} closes${n}`}
                  aria-invalid={issues.length > 0 || undefined}
                  aria-describedby={errId}
                  value={r[1]}
                  disabled={disabled}
                  onChange={(e) => setRange(i, 1, e.target.value)}
                />
                {value.ranges.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={disabled}
                    aria-label={`Remove ${label} hours ${i + 1}`}
                    onClick={() => onChange({ ...value, ranges: value.ranges.filter((_, j) => j !== i) })}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            );
          })}
          {value.ranges.length < MAX_RANGES ? (
            <div>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={disabled}
                aria-label={`Add more hours on ${label}`}
                onClick={() => onChange({ ...value, ranges: [...value.ranges, ['13:00', '17:00']] })}
              >
                Add a split
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {issues.length ? (
        <div className="err small" id={errId} role="alert" style={{ color: 'var(--bad-ink)' }}>
          {issues.map((i) => i.message).join(' ')}
        </div>
      ) : null}
    </div>
  );
}

export function HoursGrid({
  week,
  onChange,
  issues,
  disabled,
}: {
  week: WeekForm;
  onChange: (next: WeekForm) => void;
  issues: (path: string) => FieldIssue[];
  disabled?: boolean;
}) {
  return (
    <div className="stack-sm">
      <div>
        {HOSTED_PAGE_WEEKDAYS.map((d) => (
          <DayRow key={d} day={d} value={week[d]} disabled={disabled} issues={issues(`hours.${d}`)} onChange={(next) => onChange({ ...week, [d]: next })} />
        ))}
      </div>
      <div>
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled}
          onClick={() => {
            const mon = week.mon;
            const copy = (): DayForm => ({ open: mon.open, ranges: mon.ranges.map((r) => [r[0], r[1]]) });
            onChange({ ...week, tue: copy(), wed: copy(), thu: copy(), fri: copy() });
          }}
        >
          Copy Monday to Tuesday–Friday
        </button>
      </div>
    </div>
  );
}
